import React from 'react';
import { colors, radius, shadow, spacing } from './tokens';

/**
 * Curx Card — minimal, calm, white-bg surface.
 * Props:
 *   - padding: number | string (default 24)
 *   - radius: number | string (default 16)
 *   - accentColor: optional left-border accent (4px)
 *   - style: extra inline styles
 *   - className: extra class
 *   - children: content
 *   - onClick: makes the card behave like a button (cursor + a11y)
 */
export default function Card({
  padding = spacing.lg,
  radius: r = radius.lg,
  accentColor,
  style,
  className,
  children,
  onClick,
  ...rest
}) {
  const baseStyle = {
    backgroundColor: colors.card,
    borderRadius: typeof r === 'number' ? `${r}px` : r,
    boxShadow: shadow.subtle,
    border: `1px solid ${colors.border}`,
    padding: typeof padding === 'number' ? `${padding}px` : padding,
    color: colors.textPrimary,
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
    cursor: onClick ? 'pointer' : 'default',
    ...style,
  };

  return (
    <div
      className={className}
      style={baseStyle}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      {...rest}
    >
      {accentColor && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: 4,
            backgroundColor: accentColor,
          }}
        />
      )}
      {children}
    </div>
  );
}
